import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, Calendar, Clock } from 'lucide-react';

const BlogClearAligners = () => {
  return (
    <div className="bg-white">

      {/* HERO */}
      <section className="relative h-[460px] overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1606811971618-4486d14f3f99?w=1400&q=80"
          alt="Clear Aligners vs Braces"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
        <div className="absolute inset-0 flex items-end pb-12 px-8 md:px-20">
          <div className="max-w-3xl">
            <span className="inline-block bg-blue-500 text-white text-xs font-bold px-4 py-1.5 rounded-full mb-4">DENTAL</span>
            <h1 className="text-3xl md:text-4xl font-bold text-white leading-tight drop-shadow-lg mb-4">
              Clear Aligners vs Traditional Braces: Which is Right for You?
            </h1>
            <div className="flex flex-wrap items-center gap-4 text-white/80 text-sm">
              <span className="flex items-center gap-1.5"><Calendar size={14} />12 Apr 2024</span>
              <span>•</span>
              <span className="flex items-center gap-1.5"><Clock size={14} />6 min read</span>
            </div>
          </div>
        </div>
      </section>

      {/* BREADCRUMB */}
      <div className="bg-gray-100 py-3.5">
        <div className="max-w-7xl mx-auto px-6 text-sm flex items-center gap-2">
          <Link to="/" className="text-gray-500 hover:text-[#39a5d3]">Home</Link>
          <span className="text-gray-400">/</span>
          <Link to="/blog" className="text-gray-500 hover:text-[#39a5d3]">Blogs</Link>
          <span className="text-gray-400">/</span>
          <span className="text-[#39a5d3] font-semibold">Clear Aligners vs Traditional Braces</span>
        </div>
      </div>

      {/* MAIN CONTENT + SIDEBAR */}
      <section className="py-14">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">

            {/* Article */}
            <div className="lg:col-span-2 text-gray-700 leading-relaxed">

              <p className="text-lg leading-relaxed mb-8 border-l-4 border-[#39a5d3] pl-5 bg-blue-50 py-4 pr-4 rounded-r-lg">
                If your dentist has recommended orthodontic treatment, you are likely weighing two options: clear aligners like Invisalign or traditional metal braces. Both straighten teeth effectively, but they work differently and suit different patients. Understanding the real differences — beyond the marketing — will help you make a confident, informed choice that fits your lifestyle, budget, and clinical needs.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-3 pb-2 border-b-2 border-[#39a5d3]/25">How Each System Works</h2>
              <p className="mb-4">Traditional braces use metal brackets bonded to each tooth, connected by a wire that your orthodontist tightens at regular intervals — typically every 4 to 6 weeks. The constant pressure gradually shifts teeth into alignment over a period of 18 months to 3 years depending on case complexity.</p>
              <p className="mb-8">Clear aligners use a series of custom-made, removable plastic trays. Each tray moves your teeth a fraction of a millimetre, and you switch to a new tray every one to two weeks. Treatment typically runs 12 to 18 months for moderate cases, though complex cases can take longer. The key difference is that aligners are nearly invisible and can be removed for eating and cleaning.</p>

              <h2 className="text-2xl font-bold text-gray-900 mb-3 pb-2 border-b-2 border-[#39a5d3]/25">Head-to-Head Comparison</h2>
              <div className="space-y-3 mb-10">
                {[
                  { t: 'Appearance', d: 'Clear aligners are virtually invisible — most people will not notice them at a conversational distance. Traditional braces are clearly visible, though tooth-coloured ceramic brackets reduce this. For adults in professional settings or those with social concerns, this is often the deciding factor.' },
                  { t: 'Comfort', d: 'Both options cause some initial discomfort as teeth begin to move. Traditional braces can cause irritation from brackets and wires, particularly after tightening appointments. Aligners have smooth edges and generally cause less soft-tissue irritation, though each new tray creates pressure for a day or two.' },
                  { t: 'Discipline Required', d: 'This is the most critical difference. Traditional braces are fixed — they work 24 hours a day whether you like it or not. Clear aligners require 20 to 22 hours of wear per day to be effective. Patients who remove their trays too frequently — for meals, social situations, or discomfort — will extend their treatment significantly or fail to achieve the desired result.' },
                  { t: 'Oral Hygiene', d: 'Braces make thorough cleaning considerably harder. Food gets trapped around brackets and wires, increasing the risk of decay and gum disease if oral hygiene slips. Aligners are removed for cleaning, so you brush and floss normally — a major advantage for patients already prone to gum issues.' },
                  { t: 'Clinical Effectiveness', d: 'For mild to moderate crowding, spacing, and many bite issues, clear aligners now achieve results comparable to braces. However, for severe crowding, complex bite corrections, significant rotations, and cases requiring precise molar movement, traditional braces remain more predictable and controllable. Your orthodontist is the only person who can accurately assess which system will work for your specific clinical situation.' },
                  { t: 'Cost', d: 'In India, traditional braces cost approximately ₹25,000 to ₹80,000 depending on whether you choose metal, ceramic, or lingual (behind-the-teeth) brackets. Clear aligners typically range from ₹80,000 to ₹3,00,000 depending on the brand and case complexity. The cost difference reflects the technology involved in manufacturing custom aligner sets, though prices have come down significantly in recent years.' },
                  { t: 'Treatment Duration', d: 'Both options typically run 12 to 24 months for most cases, though braces can handle complex cases more efficiently in some situations. Aligner treatment can be faster for straightforward cases when patients comply fully with wear instructions.' },
                ].map((item, i) => (
                  <div key={i} className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                    <p className="font-bold text-gray-900 mb-1.5">{item.t}</p>
                    <p className="text-gray-600 text-sm leading-relaxed">{item.d}</p>
                  </div>
                ))}
              </div>

              {/* Mid article image */}
              <div className="my-10 rounded-2xl overflow-hidden shadow-lg border border-gray-100">
                <img
                  src="/images/15.png"
                  alt="Dental orthodontic treatment"
                  className="w-full h-72 object-cover"
                />
                <div className="bg-gray-50 px-4 py-2.5 border-t border-gray-100">
                  <p className="text-xs text-gray-500 italic text-center">Modern orthodontic options have made teeth straightening more accessible and comfortable than ever before.</p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-[#39a5d3]/25">Who Should Choose Clear Aligners?</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-10">
                {[
                  'Adults and older teenagers who can commit to 20-22 hours of daily wear',
                  'Patients with mild to moderate crowding or spacing issues',
                  'Those in professional environments where aesthetics matter',
                  'Patients with good oral hygiene who want to maintain easy cleaning routines',
                  'People who play contact sports or wind instruments',
                  'Patients who have had braces before and need minor refinement',
                ].map((s, i) => (
                  <div key={i} className="flex items-start gap-3 bg-white border border-gray-200 rounded-lg px-4 py-3">
                    <span className="w-2 h-2 rounded-full bg-[#39a5d3] mt-1.5 flex-shrink-0" />
                    <p className="text-sm">{s}</p>
                  </div>
                ))}
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 pb-2 border-b-2 border-[#39a5d3]/25">Who Should Choose Traditional Braces?</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-10">
                {[
                  'Children and younger teenagers who may not comply with aligner wear requirements',
                  'Patients with severe crowding, significant rotations, or complex bite issues',
                  'Those requiring precise molar movement or jaw realignment',
                  'Budget-conscious patients for whom cost is the primary consideration',
                  'Patients who prefer a hands-off approach with no daily compliance requirement',
                  'Cases where extractions are required as part of the treatment plan',
                ].map((s, i) => (
                  <div key={i} className="flex items-start gap-3 bg-white border border-gray-200 rounded-lg px-4 py-3">
                    <span className="w-2 h-2 rounded-full bg-[#19354a] mt-1.5 flex-shrink-0" />
                    <p className="text-sm">{s}</p>
                  </div>
                ))}
              </div>

              <div className="bg-blue-50 border border-blue-100 rounded-2xl p-7 mb-10">
                <h2 className="text-xl font-bold text-gray-900 mb-3">The Most Important Factor: Your Orthodontist's Assessment</h2>
                <p className="text-sm leading-relaxed text-gray-600 mb-4">
                  No blog article or online quiz can tell you which treatment is right for you. Only a qualified dentist or orthodontist who has examined your teeth, reviewed X-rays, and assessed your bite can make that determination. Be wary of any provider who recommends clear aligners without a thorough clinical examination — not every case is suitable, and an inappropriate treatment choice can cause harm.
                </p>
                <ul className="space-y-2">
                  {[
                    'Ask your dentist or orthodontist which option they recommend for your specific case and why',
                    'Get a second opinion if you are uncertain — this is a significant financial and time investment',
                    'Ask to see before-and-after photos of similar cases treated at that clinic',
                    'Understand the full cost including retainers, which are required after both treatments',
                    'Clarify the number of appointments required and the practice\'s out-of-hours contact policy',
                  ].map((p, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm text-gray-700">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#39a5d3] mt-2 flex-shrink-0" />
                      {p}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-10 flex items-center justify-between">
                <Link to="/blog" className="inline-flex items-center gap-2 text-[#39a5d3] font-semibold text-sm hover:underline">
                  <ChevronLeft size={16} /> Back to All Blogs
                </Link>
               
              </div>
            </div>

            {/* Sidebar */}
            <aside className="space-y-7">
              <div className="bg-white rounded-xl shadow-md border border-gray-100">
                <div className="bg-[#19354a] px-5 py-4 rounded-t-xl">
                  <h3 className="text-white font-bold text-sm">Key Facts</h3>
                </div>
                <div className="p-5 space-y-3">
                  {[
                    { l: 'Clear aligner cost in India', v: '₹80K–3L' },
                    { l: 'Traditional braces cost', v: '₹25K–80K' },
                    { l: 'Required daily aligner wear', v: '20–22 hrs' },
                    { l: 'Average treatment duration', v: '12–24 months' },
                  ].map((f, i) => (
                    <div key={i} className="flex justify-between border-b border-gray-100 pb-3 last:border-0">
                      <p className="text-xs text-gray-500">{f.l}</p>
                      <p className="text-xs font-bold text-[#39a5d3]">{f.v}</p>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-xl shadow-md border border-gray-100">
                <div className="bg-[#19354a] px-5 py-4 rounded-t-xl">
                  <h3 className="text-white font-bold text-sm">Quick Comparison</h3>
                </div>
                <div className="p-5 space-y-3">
                  {[
                    { label: 'Visibility', braces: 'Visible', aligners: 'Nearly invisible' },
                    { label: 'Removable', braces: 'No', aligners: 'Yes' },
                    { label: 'Cleaning', braces: 'Harder', aligners: 'Normal' },
                    { label: 'Complex cases', braces: 'Better', aligners: 'Moderate cases' },
                    { label: 'Cost', braces: 'Lower', aligners: 'Higher' },
                  ].map((row, i) => (
                    <div key={i} className="border-b border-gray-100 pb-3 last:border-0">
                      <p className="text-xs font-bold text-gray-700 mb-1">{row.label}</p>
                      <div className="flex justify-between">
                        <span className="text-xs text-gray-500">Braces: {row.braces}</span>
                        <span className="text-xs text-[#39a5d3] font-medium">Aligners: {row.aligners}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-[#19354a] rounded-xl p-6 text-white text-center">
                <h3 className="font-bold mb-2">Book a Dental Consultation</h3>
                <p className="text-white/80 text-xs mb-4">Find verified dentists and orthodontists on Docmize.</p>
                <Link to="/doctors?specialty=Dentist" className="block bg-white text-[#19354a] font-bold py-2.5 rounded-lg text-sm hover:bg-gray-100 transition-colors">
                  Find a Dentist
                </Link>
                <div className="mt-4 pt-3 border-t border-white/20">
                  <p className="text-white/70 text-xs">Call us anytime</p>
                  <a href="tel:+918920845297" className="text-white font-bold text-sm">+91 89208 45297</a>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogClearAligners;